/**
 * Central query-key registry.
 *
 * Keys live in one place so that a mutation in one feature can invalidate a
 * cache owned by another (approving scope refreshes the dashboard KPIs, for
 * instance) without features importing each other.
 */
export const queryKeys = {
  session: () => ['session'] as const,

  users: {
    all: () => ['users'] as const,
    directory: (search?: string) => ['users', 'directory', search ?? ''] as const,
  },

  projects: {
    all: () => ['projects'] as const,
    list: () => ['projects', 'list'] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
  },

  dashboard: {
    all: () => ['dashboard'] as const,
    /**
     * One key for the whole Command Center: the API answers KPIs, charts and
     * activity together, so the summary and the feed share a single cache entry.
     */
    commandCenter: () => ['dashboard', 'command-center'] as const,
  },

  analyses: {
    all: () => ['analyses'] as const,
    list: () => ['analyses', 'list'] as const,
    detail: (id: string) => ['analyses', 'detail', id] as const,
  },

  testScripts: {
    all: () => ['test-scripts'] as const,
    list: (projectId?: string) => ['test-scripts', 'list', projectId ?? 'all'] as const,
    detail: (id: string) => ['test-scripts', 'detail', id] as const,
  },

  catalog: {
    all: () => ['catalog'] as const,
    platforms: () => ['catalog', 'platforms'] as const,
    regions: () => ['catalog', 'regions'] as const,
    modules: () => ['catalog', 'modules'] as const,
    testTypes: () => ['catalog', 'test-types'] as const,
    models: () => ['catalog', 'models'] as const,
  },

  admin: {
    all: () => ['admin'] as const,
    promptTemplate: (capability: string, module: string, subType: string) =>
      ['admin', 'prompts', capability, module, subType] as const,
    promptIndex: () => ['admin', 'prompts', 'index'] as const,
    llmConfigs: () => ['admin', 'llm-configs'] as const,
    adGroups: () => ['admin', 'ad-groups'] as const,
    featureFlags: () => ['admin', 'feature-flags'] as const,
    usage: () => ['admin', 'usage'] as const,
    audit: () => ['admin', 'audit'] as const,
  },
} as const;
